import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Loader2, AlertCircle, Check, Clock, AlertTriangle, AlertOctagon, Info } from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';

const SanitizationManagement = ({ table, onStatusUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sanitizedColumns, setSanitizedColumns] = useState([]);
  const [indexedColumns, setIndexedColumns] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [tableName, setTableName] = useState(null);
  const [showRequireIndexMessage, setShowRequireIndexMessage] = useState(false);
  const { config } = useConfig();

  // Fetch initial sanitization configuration and indexed columns
  useEffect(() => {
    const fetchSanitizationConfig = async () => {
      try {
        setLoading(true);
        setError(null);
     
        // First get the table name from the configuration
        const tablesDoc = await getDoc(doc(db, 'appConfiguration', 'tables'));
        const tablesData = tablesDoc.data();
        const foundTableName = Object.keys(tablesData).find(name => tablesData[name].id === table.id);
        setTableName(foundTableName);
  
        // Get the sanitization configuration
        const sanitizationDoc = await getDoc(doc(db, 'appConfiguration', 'sanitization'));
        const sanitizationConfig = sanitizationDoc.exists() ? sanitizationDoc.data() : {};
        const tableConfig = sanitizationConfig[foundTableName] || { id: table.id, columns: [] };
        setSanitizedColumns(tableConfig.columns || []);

        // Get indexed columns to verify only indexed columns can be sanitized
        const indexingDoc = await getDoc(doc(db, 'appConfiguration', 'indexing'));
        const indexingConfig = indexingDoc.exists() ? indexingDoc.data() : {};
        const indexedTableConfig = indexingConfig[foundTableName] || { id: table.id, columns: [] };
        setIndexedColumns(indexedTableConfig.columns || []);
   
      } catch (err) {
        console.error('Error fetching sanitization configuration:', err);
        setError('Failed to load sanitization configuration');
      } finally {
        setLoading(false);
      }
    };
   
    fetchSanitizationConfig();
  }, [table.id]);
   
  const handleToggleColumn = async (columnName) => {
    try {
      // Check if column is indexed before allowing sanitization
      if (!indexedColumns.includes(columnName)) {
        setShowRequireIndexMessage(true);
        
        if (onStatusUpdate) {
          onStatusUpdate({
            type: 'error',
            message: `Column ${columnName} must be indexed before it can be sanitized`
          });
        }
        
        // Hide message after 5 seconds
        setTimeout(() => setShowRequireIndexMessage(false), 5000);
        return;
      }
      
      setUpdating(true);
      setError(null);
  
      const sanitizationDoc = await getDoc(doc(db, 'appConfiguration', 'sanitization'));
      const sanitizationConfig = sanitizationDoc.exists() ? sanitizationDoc.data() : {};
      const currentColumns = (sanitizationConfig[tableName]?.columns) || [];
  
      let updatedColumns;
      if (currentColumns.includes(columnName)) {
        updatedColumns = currentColumns.filter(col => col !== columnName);
      } else {
        updatedColumns = [...currentColumns, columnName];
      }
  
      // If the document doesn't exist yet, create it
      if (!sanitizationDoc.exists()) {
        await updateDoc(doc(db, 'appConfiguration', 'sanitization'), {
          [tableName]: {
            id: table.id,
            columns: updatedColumns
          }
        }).catch(async (error) => {
          // If the document doesn't exist yet, create it
          if (error.code === 'not-found') {
            const sanitizationRef = doc(db, 'appConfiguration', 'sanitization');
            await setDoc(sanitizationRef, {
              [tableName]: {
                id: table.id,
                columns: updatedColumns
              }
            });
          } else {
            throw error;
          }
        });
      } else {
        await updateDoc(doc(db, 'appConfiguration', 'sanitization'), {
          [tableName]: {
            id: table.id,
            columns: updatedColumns
          }
        });
      }
  
      setSanitizedColumns(updatedColumns);
      
      if (onStatusUpdate) {
        onStatusUpdate({
          type: 'success',
          message: `Column ${columnName} ${updatedColumns.includes(columnName) ? 'added to' : 'removed from'} sanitization`
        });
      }
   
    } catch (err) {
      console.error('Error updating sanitization configuration:', err);
      setError('Failed to update sanitization configuration');
      if (onStatusUpdate) {
        onStatusUpdate({
          type: 'error',
          message: 'Failed to update sanitization configuration'
        });
      }
    } finally {
      setUpdating(false);
    }
  };

  const getColumnStatus = (columnName) => {
    // Check if column is not indexed
    if (!indexedColumns.includes(columnName)) {
      return {
        status: 'not-indexed',
        icon: <AlertOctagon className="h-4 w-4 text-orange-500" />,
        text: 'Requires Indexing'
      };
    }
    
    // Check if column is sanitized
    if (sanitizedColumns.includes(columnName)) {
      return {
        status: 'sanitized',
        icon: <Check className="h-4 w-4 text-green-500" />,
        text: 'Sanitized'
      };
    }
    
    // Column is indexed but not sanitized
    return {
      status: 'not-sanitized',
      icon: <AlertTriangle className="h-4 w-4 text-gray-400" />,
      text: 'Not Sanitized'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-2 text-red-500 py-4">
        <AlertCircle className="h-5 w-5" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header with explanation */}
      <div className="mb-4 px-4 py-2 bg-gray-50 rounded-t-lg">
        <p className="text-sm text-gray-700">
          Select columns containing sensitive data that should be sanitized before sending to the LLM.
          Values in these columns will be replaced with placeholders like COLUMN_NAME_1 in prompts.
        </p>
        <p className="text-sm text-gray-700 mt-2 font-medium flex items-center">
          <Info className="h-4 w-4 mr-1.5 text-blue-500" />
          Note: Columns must be indexed before they can be sanitized.
        </p>
      </div>

      {/* Show warning message if trying to sanitize non-indexed column */}
      {showRequireIndexMessage && (
        <div className="mb-4 px-4 py-2 bg-orange-50 border border-orange-200 rounded-md">
          <p className="text-sm text-orange-700 flex items-center">
            <AlertOctagon className="h-4 w-4 mr-1.5" />
            Columns must be indexed before they can be sanitized. Please index this column first using the Indexing section.
          </p>
        </div>
      )}

      {/* Main Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Column Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Sanitized
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.schema.map((field, index) => {
              const columnStatus = getColumnStatus(field.name);
              return (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {field.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {field.type}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleToggleColumn(field.name)}
                      disabled={updating || !indexedColumns.includes(field.name)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                        sanitizedColumns.includes(field.name) ? 'bg-primary' : 
                        !indexedColumns.includes(field.name) ? 'bg-gray-300 cursor-not-allowed opacity-50' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          sanitizedColumns.includes(field.name) ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center space-x-2">
                      {columnStatus.icon}
                      <span className={`text-sm ${
                        columnStatus.status === 'sanitized' ? 'text-green-700' : 
                        columnStatus.status === 'not-indexed' ? 'text-orange-700' : 'text-gray-500'
                      }`}>
                        {columnStatus.text}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SanitizationManagement;